const PAYU_SCRIPT_URL = 'https://static.payu.com/res/v2/widget-mini-installments.js';

function loadPayUInstallments() {
  if (window.OpenPayU?.Installments?.miniInstallment) return Promise.resolve();
  if (window.__vhPayUFinancingScriptPromise) return window.__vhPayUFinancingScriptPromise;

  window.__vhPayUFinancingScriptPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${PAYU_SCRIPT_URL}"]`);
    let timeoutId;

    const finish = () => {
      clearTimeout(timeoutId);
      if (window.OpenPayU?.Installments?.miniInstallment) resolve();
      else reject(new Error('PayU installments script did not initialize.'));
    };

    const fail = () => {
      clearTimeout(timeoutId);
      reject(new Error('PayU installments script could not be loaded.'));
    };

    if (!script) {
      script = document.createElement('script');
      script.src = PAYU_SCRIPT_URL;
      script.async = true;
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', fail, { once: true });
      document.head.appendChild(script);
    } else if (window.OpenPayU?.Installments?.miniInstallment) {
      finish();
    } else {
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', fail, { once: true });
    }

    timeoutId = window.setTimeout(fail, 8000);
  }).catch((error) => {
    window.__vhPayUFinancingScriptPromise = null;
    throw error;
  });

  return window.__vhPayUFinancingScriptPromise;
}

class ValleansPayUCalculator extends HTMLElement {
  #abortController = new AbortController();
  #renderToken = 0;
  #i18n = {};

  connectedCallback() {
    const form = this.querySelector('[data-payu-form]');
    if (!(form instanceof HTMLFormElement)) return;

    this.#readTranslations();
    this.#prepareInstructions(form);
    form.addEventListener('submit', this.#onSubmit, { signal: this.#abortController.signal });
    this.#setStatus(this.#t('initialStatus'));
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #readTranslations() {
    const source = this.querySelector('[data-payu-i18n]');
    if (!(source instanceof HTMLScriptElement)) return;
    try {
      this.#i18n = JSON.parse(source.textContent || '{}');
    } catch (error) {
      this.#i18n = {};
      console.error('[Valleans PayU financing] Invalid translation JSON', error);
    }
  }

  #t(key) {
    return typeof this.#i18n[key] === 'string' ? this.#i18n[key] : '';
  }

  #prepareInstructions(form) {
    const label = form.querySelector('label');
    const button = form.querySelector('button[type="submit"]');
    if (label) label.textContent = this.#t('amountLabel');
    if (button) button.textContent = this.#t('showOptions');

    if (!this.querySelector('[data-payu-guide]')) {
      const guide = document.createElement('div');
      guide.className = 'vh-payu-calculator__guide';
      guide.dataset.payuGuide = 'true';
      guide.innerHTML = '<div><span>1</span><strong></strong></div><div><span>2</span><strong></strong></div><div><span>3</span><strong></strong></div>';
      const labels = guide.querySelectorAll('strong');
      if (labels[0]) labels[0].textContent = this.#t('guide1');
      if (labels[1]) labels[1].textContent = this.#t('guide2');
      if (labels[2]) labels[2].textContent = this.#t('guide3');
      form.before(guide);
    }
  }

  #onSubmit = (event) => {
    event.preventDefault();
    this.#render(this.#amount());
  };

  #amount() {
    const input = this.querySelector('[data-payu-amount]');
    if (!(input instanceof HTMLInputElement)) return Number(this.dataset.initialAmount || 0);
    return Number(String(input.value).replace(',', '.'));
  }

  #setStatus(message, hidden = false) {
    const status = this.querySelector('[data-payu-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.hidden = hidden;
  }

  #createNextStep() {
    const notice = document.createElement('div');
    notice.className = 'vh-payu-calculator__next-step';

    const number = document.createElement('span');
    number.className = 'vh-payu-calculator__next-number';
    number.textContent = '3';

    const copy = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = this.#t('nextTitle');
    const text = document.createElement('p');
    text.textContent = this.#t('nextText');
    copy.append(title, text);

    const arrow = document.createElement('span');
    arrow.className = 'vh-payu-calculator__next-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '↓';

    notice.append(number, copy, arrow);
    return notice;
  }

  async #render(creditAmount) {
    const resultMount = this.querySelector('[data-payu-result]');
    if (!(resultMount instanceof HTMLElement)) return;

    if (!Number.isFinite(creditAmount) || creditAmount <= 0) {
      resultMount.replaceChildren();
      this.#setStatus(this.#t('invalidAmount'));
      return;
    }

    const posId = this.dataset.posId || '';
    const key = this.dataset.key || '';
    if (!posId || !key) {
      resultMount.replaceChildren();
      this.#setStatus(this.#t('unavailable'));
      return;
    }

    const token = ++this.#renderToken;
    resultMount.replaceChildren();
    this.#setStatus(this.#t('loading'));

    try {
      await loadPayUInstallments();
      if (token !== this.#renderToken || !this.isConnected) return;

      const result = await window.OpenPayU.Installments.miniInstallment('', {
        creditAmount,
        posId,
        key,
        showLongDescription: true,
        currencySign: 'PLN',
        lang: this.dataset.payuLang || 'en',
      });

      if (token !== this.#renderToken || !this.isConnected) return;
      resultMount.replaceChildren();

      if (result?.isWidgetAvailable && result.element) {
        resultMount.appendChild(this.#createNextStep());
        resultMount.appendChild(result.element);
        this.#setStatus('', true);
        window.requestAnimationFrame(() => {
          resultMount.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        this.#setStatus(this.#t('noSimulation'));
      }
    } catch (error) {
      if (token !== this.#renderToken || !this.isConnected) return;
      resultMount.replaceChildren();
      this.#setStatus(this.#t('error'));
      console.error('[Valleans PayU financing]', error);
    }
  }
}

if (!customElements.get('valleans-payu-calculator')) {
  customElements.define('valleans-payu-calculator', ValleansPayUCalculator);
}
